import { ref } from "vue";
import type { User } from "@firebase/auth";
import { auth, db, functions } from "./firebase";
import { httpsCallable } from "@firebase/functions";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut as firebaseSignOut,
  signInWithCustomToken,
} from "@firebase/auth";
import { deleteUser, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { SAMLAuthProvider } from "firebase/auth";
import { onAuthStateChanged as firebaseOnAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { z } from "zod";
import posthog from "posthog-js";
import { createClient } from "@liveblocks/client";
import { type CustomAuthenticationResult } from "@liveblocks/core";

/**
 * Drop any PostHog feature-flag overrides and identity on logout.
 *
 * Overrides written via `posthog.featureFlags.override(...)` (in userst.ts, for
 * users with team/automation/personal flags) are persisted by posthog-js to BOTH
 * localStorage AND a cross-subdomain `.briefly.bio` cookie. Without this, an
 * override outlives the user that caused it — leaking into the next user or even
 * the logged-out app, and surviving a localStorage clear via the cookie.
 */
function resetPosthogOnLogout() {
  posthog.featureFlags.override(false);
  posthog.reset();
}

const liveblocksAuth = httpsCallable<
  { room: string },
  CustomAuthenticationResult
>(functions, "liveblocksAuth");

export async function resendVerificationEmail(email: string) {
  console.log("Resending verification email to", email);
  const user = await signInWithEmailAndPassword(auth, email, "");
  if (user.user) {
    await sendEmailVerification(user.user);
  } else {
    throw new Error("User not found");
  }
}

export const liveblocksClient = createClient({
  authEndpoint: async (room) =>
    room
      ? (await liveblocksAuth({ room })).data
      : { error: "Can't create liveblocksAuth", reason: "No room specified" },
});

// Logs the current user's ID token (JWT) to the console for debugging Firestore requests
export async function logCurrentIdToken(context: string) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log(`[Token][${context}] No authenticated user`);
      return;
    }
    const token = await user.getIdToken();
    console.log(`[Token][${context}]`, token);
  } catch (e) {
    console.warn(`[Token][${context}] Failed to get token`, e);
  }
}

// Logs the values used by rules: request.auth.token.auth_time vs users/{uid}.sessionValidFrom.seconds
export async function logAuthSessionDebug(context: string = "debug") {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log(`[RulesDebug][${context}] No authenticated user`);
      return;
    }
    // Force refresh to ensure latest token and claims
    const tokenResult = await user.getIdTokenResult();
    // tokenResult.authTime is an RFC 3339 string; convert to epoch seconds
    const authTimeSeconds = Math.floor(
      new Date(tokenResult.authTime).getTime() / 1000
    );
    const issuedAtSeconds = Math.floor(
      new Date(tokenResult.issuedAtTime).getTime() / 1000
    );

    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);
    let sessionSeconds: number | null = null;
    if (snap.exists()) {
      const svf: any = (snap.data() as any).sessionValidFrom;
      if (svf) {
        if (typeof svf.seconds === "number") {
          sessionSeconds = svf.seconds;
        } else if (typeof svf.toMillis === "function") {
          sessionSeconds = Math.floor(svf.toMillis() / 1000);
        }
      }
    }

    console.log(`[RulesDebug][${context}] auth_time (s):`, authTimeSeconds);
    console.log(`[RulesDebug][${context}] issued_at (s):`, issuedAtSeconds);
    console.log(
      `[RulesDebug][${context}] sessionValidFrom.seconds:`,
      sessionSeconds
    );
    const comparison =
      sessionSeconds == null ? true : authTimeSeconds > sessionSeconds;
    console.log(
      `[RulesDebug][${context}] isAuthSessionValid comparison:`,
      `${authTimeSeconds} > ${sessionSeconds} =>`,
      comparison
    );
  } catch (e) {
    console.warn(`[RulesDebug][${context}] Failed to log auth/session`, e);
  }
}

export async function signUp(email: string, password: string) {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    await sendEmailVerification(userCredential.user);
    return userCredential;
  } catch (error: any) {
    // Also handle other password-related Firebase Auth errors
    if (error.code === "auth/password-does-not-meet-requirements") {
      throw new Error(error.message);
    }

    // For email-related errors or any other errors, return a generic message
    // to prevent email enumeration
    throw new Error(
      "We couldn't create your account. If you already have an account, please try logging in instead. For further assistance, contact support at team@briefly.bio."
    );
  }
}

export async function signIn(email: string, password: string) {
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );
  const user = userCredential.user;

  if (!user.emailVerified) {
    await sendEmailVerification(user);
    setTimeout(() => {
      signOut();
    }, 3000);
    throw new Error(
      "Your account is not verified. A verification email has been sent."
    );
  }

  return userCredential;
}

/**
 * Set true synchronously before client sign-out so views can suppress the
 * transient `permission-denied` "No access" screen during logout. `signOut()`
 * always ends in a full page reload, so this never needs resetting.
 */
export const isLoggingOut = ref(false);

export async function signOut() {
  const currentPath = window.location.pathname;
  // Call server-side logout first to revoke refresh tokens and set sessionValidFrom
  const callLogout = httpsCallable<
    unknown,
    { status: string; sessionValidFrom: string }
  >(functions, "logoutV2");
  try {
    await callLogout({});
  } catch (e) {
    console.error("Logout callable failed", e);
    // Do not proceed with client sign-out if server logout fails
    // to avoid leaving tokens valid server-side without a cutoff.
    throw new Error("Failed to log out securely. Please try again.");
  }

  isLoggingOut.value = true;
  await firebaseSignOut(auth);
  resetPosthogOnLogout();
  window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
}

export function signOutSilent() {
  firebaseSignOut(auth);
  resetPosthogOnLogout();
}

export function onAuthStateChanged(callback: (user: User | null) => void) {
  return firebaseOnAuthStateChanged(auth, callback);
}

export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const userDoc = await getDoc(doc(db, `users/${user.uid}`));
    if (!userDoc.exists()) {
      console.error(
        "User does not exist in the database, please sign up first."
      );
      await deleteUser(user);
      await auth.signOut();
      return;
    }

    return result;
  } catch (error) {
    console.error("Error during Google sign-in:", error);
    alert("An error occurred during sign-in. Please try again.");
  }
}

/**
 * Start a SAML SSO sign-in and return the signed-in user.
 *
 * We use a **popup**, not a redirect. `signInWithRedirect` stashes its result in
 * storage on the Firebase authDomain (…firebaseapp.com) and the app must read it
 * back cross-domain; when the app is served on a *different* origin (web.app, or
 * a custom domain like app.briefly.bio) modern browsers partition that storage
 * and the result is silently lost — the user just loops back to /sso. The popup
 * returns the credential directly to the opener, so it works regardless of which
 * domain the app is served on.
 *
 * Like Google this auto-provisions first-time users server-side (the Part-2
 * blocking function + onUserCreated JIT), so — unlike signInWithGoogle — we do
 * NOT do the "delete if no user doc" check. Errors (invalid provider, popup
 * closed, or the blocking function denying a mismatched domain) propagate to the
 * caller so the SSO screen can show them inline.
 */
export async function signInWithSaml(
  providerId: string
): Promise<User | null> {
  const provider = new SAMLAuthProvider(providerId); // e.g. "saml.acme"
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export const createWorkspaceSchema = (
  wording: "Username" | "Workspace" = "Workspace"
) =>
  z
    .string()
    .min(5, `${wording} must be at least 5 characters`)
    .max(100, `${wording} must be at most 100 characters`)
    .refine((value) => /^[A-Za-z0-9 _-]+$/.test(value), {
      message:
        "Only letters, numbers,hyphen (-), underscore (_) and spaces are allowed",
    })
    .transform((w) => w.toLowerCase().replace(/ /g, "-").replace(/_/g, "-"))
    .refine(workspaceAvailable, (val: string) => ({
      message: `${wording} ${val} is already taken`,
    }));

export const workspaceAvailable = async (username: string) => {
  if (!username) return false;
  const collectionRef = doc(db, `workspaces/${username}`);
  await logCurrentIdToken("workspaceAvailable");
  const docSnap = await getDoc(collectionRef);
  return !docSnap.exists();
};

export const userExists = async (uid: string) => {
  if (!uid) return false;
  const collectionRef = doc(db, `users/${uid}`);
  await logCurrentIdToken("userExists");
  const docSnap = await getDoc(collectionRef);
  return docSnap.exists();
};

export async function fetchUserProfile(userId: string) {
  const userRef = doc(db, "users", userId);
  await logCurrentIdToken("fetchUserProfile");
  const userDoc = await getDoc(userRef);
  if (userDoc.exists()) {
    return userDoc.data();
  } else {
    throw new Error("User not found");
  }
}

/**
 * Start impersonating another user. Only available to super admins.
 * @param targetIdentifier - Either email or userId of the target user
 * @param existingToken - Optional token if already generated (used by URL-based impersonation)
 */
export async function startImpersonation(
  targetIdentifier: string,
  existingToken?: string
) {
  const adminUser = auth.currentUser;
  if (!adminUser) throw new Error("No user logged in");

  // Check super admin claim
  await adminUser.getIdToken(true);
  const tokenResult = await adminUser.getIdTokenResult();
  const isSuperAdmin = tokenResult.claims?.["superAdmin"] === true;
  if (!isSuperAdmin) {
    throw new Error("Unauthorized: Only super admins can impersonate users");
  }

  let impersonationToken: string;

  if (existingToken) {
    impersonationToken = existingToken;
  } else {
    // Determine if targetIdentifier is an email
    const isEmail = targetIdentifier.includes("@");

    // Get target user's auth token
    const {
      data: { token },
    } = await httpsCallable<
      { targetUserId?: string; targetEmail?: string },
      { token: string }
    >(
      functions,
      "generateImpersonationTokenV2"
    )(
      isEmail
        ? { targetEmail: targetIdentifier }
        : { targetUserId: targetIdentifier }
    );

    impersonationToken = token;
  }

  // Sign in with target user's token
  await signInWithCustomToken(auth, impersonationToken);

  // Store original admin ID in session storage
  sessionStorage.setItem("impersonator", adminUser.uid);
}

/**
 * Check if the current session is an impersonation
 */
export function isImpersonating(): boolean {
  return sessionStorage.getItem("impersonator") !== null;
}

// Expose debug helpers for console usage
// Usage in DevTools:
//   authDebug.logAuthSessionDebug("manual");
//   authDebug.logCurrentIdToken("manual");
if (typeof window !== "undefined") {
  // @ts-ignore - attach for manual debugging
  (window as any).authDebug = {
    logAuthSessionDebug,
    logCurrentIdToken,
  };
}
